<script setup>
import { computed } from 'vue';
import Avatar from 'next/avatar/Avatar.vue';
import { useI18n } from 'vue-i18n';

import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  macro: {
    type: Object,
    required: true,
  },
});
defineEmits(['delete']);
const { t } = useI18n();

const createdByName = computed(() => {
  const createdBy = props.macro.created_by;
  return createdBy.available_name ?? createdBy.email ?? '';
});

const updatedByName = computed(() => {
  const updatedBy = props.macro.updated_by;
  return updatedBy.available_name ?? updatedBy.email ?? '';
});

const isGlobal = computed(() => props.macro.visibility === 'global');

const visibilityLabel = computed(() => {
  const i18nKey = isGlobal.value
    ? 'MACROS.EDITOR.VISIBILITY.GLOBAL.LABEL'
    : 'MACROS.EDITOR.VISIBILITY.PERSONAL.LABEL';
  return t(i18nKey);
});
</script>

<template>
  <tr class="group hover:bg-n-solid-3 transition-colors">
    <!-- w-full + max-w-0: nome absorve o espaco restante e trunca — evita a
         tabela estourar o card e esconder a coluna de acoes (mesmo bug das
         respostas prontas). -->
    <td class="px-5 py-3 w-full max-w-0 align-middle">
      <span class="block text-sm font-medium text-n-slate-12 truncate">
        {{ macro.name }}
      </span>
    </td>
    <td class="px-5 py-3 whitespace-nowrap align-middle">
      <div v-if="macro.created_by" class="flex items-center gap-2">
        <Avatar :name="createdByName" :size="24" rounded-full />
        <span class="text-sm text-n-slate-11">{{ createdByName }}</span>
      </div>
      <span v-else class="text-sm text-n-slate-8">--</span>
    </td>
    <td class="px-5 py-3 whitespace-nowrap align-middle">
      <div v-if="macro.updated_by" class="flex items-center gap-2">
        <Avatar :name="updatedByName" :size="24" rounded-full />
        <span class="text-sm text-n-slate-11">{{ updatedByName }}</span>
      </div>
      <span v-else class="text-sm text-n-slate-8">--</span>
    </td>
    <td class="px-5 py-3 whitespace-nowrap align-middle">
      <span
        class="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium"
        :class="
          isGlobal
            ? 'bg-n-teal-3 text-n-teal-11'
            : 'bg-n-alpha-2 text-n-slate-11'
        "
      >
        <span
          class="size-3"
          :class="isGlobal ? 'i-lucide-globe' : 'i-lucide-lock'"
        />
        {{ visibilityLabel }}
      </span>
    </td>
    <td class="px-5 py-3 w-px whitespace-nowrap align-middle">
      <div
        class="flex justify-end gap-1 md:opacity-0 md:group-hover:opacity-100 md:focus-within:opacity-100 transition-opacity"
      >
        <router-link
          :to="{ name: 'macros_edit', params: { macroId: macro.id } }"
        >
          <Button
            v-tooltip.top="$t('MACROS.EDIT.TOOLTIP')"
            icon="i-lucide-pen"
            slate
            xs
            faded
          />
        </router-link>
        <Button
          v-tooltip.top="$t('MACROS.DELETE.TOOLTIP')"
          icon="i-lucide-trash-2"
          xs
          ruby
          faded
          @click="$emit('delete')"
        />
      </div>
    </td>
  </tr>
</template>
